import { defineStore } from 'pinia'
import { nextTick, ref } from 'vue'

export const useHydrationStore = defineStore('hydration', () => {
  const hydrateFunctions = ref<(() => void)[]>([])
  const hasNavigated = ref(false)
  let navigationResolver: (() => void) | null = null
  let mutationTimeout: NodeJS.Timeout | null = null
  let observer: MutationObserver | null = null

  function registerHydrateFunction(fn: () => void) {
    if (hasNavigated.value) {
      // If navigation has occurred, hydrate immediately
      fn()
    } else {
      hydrateFunctions.value.push(fn)
    }
  }

  function resolveAllHydrations(): void {
    if (hydrateFunctions.value.length === 0) {
      navigationResolver?.()
      return
    }

    // Create a mutation observer to detect DOM changes
    observer = new MutationObserver(() => {
      // Reset the timeout on each mutation
      if (mutationTimeout) {
        clearTimeout(mutationTimeout)
      }

      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Async callback in setTimeout is intentional
      mutationTimeout = setTimeout(async () => {
        // No mutations for 100ms, consider hydration complete
        observer?.disconnect()
        await nextTick()
        navigationResolver?.()
      }, 100)
    })

    // Start observing the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    })

    // Execute all hydrations
    hydrateFunctions.value.forEach((fn) => fn())
    hydrateFunctions.value = []

    // Set a safety timeout in case no mutations occur
    setTimeout(() => {
      observer?.disconnect()
      navigationResolver?.()
    }, 1000)
  }

  function setNavigationResolver(resolver: () => void) {
    navigationResolver = resolver
  }

  return {
    registerHydrateFunction,
    hasNavigated,
    resolveAllHydrations,
    hydrateFunctions,
    setNavigationResolver,
  }
})
